import React, { useState, useEffect, useRef } from 'react';
import { geocodeLocation } from '../services/api';
import './BirthDetailsForm.css';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const EMPTY_FORM = {
  name: '',
  day: '',
  month: '',
  year: '',
  time: '',
  location: '',
  latitude: '',
  longitude: '',
  gender: '',
};

/**
 * Birth details capture. State is seeded from the active profile and handed
 * back on submit — this component no longer writes to localStorage itself,
 * since profile persistence is owned by App.
 */
function BirthDetailsForm({ onSubmit, initialData }) {
  const [formData, setFormData] = useState(() => {
    if (!initialData) return EMPTY_FORM;
    // Stored profiles keep a combined 'date'; split it back into dropdowns.
    if (initialData.date && !initialData.day) {
      const [year, month, day] = String(initialData.date).split('-');
      return {
        ...EMPTY_FORM,
        ...initialData,
        day: day ? String(Number(day)) : '',
        month: month ? String(Number(month)) : '',
        year: year || '',
      };
    }
    return { ...EMPTY_FORM, ...initialData };
  });

  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState('');
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const setField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const handleLocationChange = (e) => {
    const value = e.target.value;
    // Typing a new query invalidates any previously chosen coordinates.
    setFormData((prev) => ({ ...prev, location: value, latitude: '', longitude: '' }));
    if (error) setError('');

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (value.trim().length >= 3) {
      searchTimeoutRef.current = setTimeout(async () => {
        setIsSearching(true);
        try {
          const suggestions = await geocodeLocation(value);
          setLocationSuggestions(suggestions);
          setShowSuggestions(true);
        } catch (err) {
          console.error('Error fetching location suggestions:', err);
          setLocationSuggestions([]);
        } finally {
          setIsSearching(false);
        }
      }, 500);
    } else {
      setLocationSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const selectLocation = (suggestion) => {
    setFormData((prev) => ({
      ...prev,
      location: suggestion.display_name,
      latitude: suggestion.lat,
      longitude: suggestion.lon,
    }));
    setShowSuggestions(false);
    setLocationSuggestions([]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name.trim()) return setError('Please enter a name.');
    if (!formData.gender) return setError('Please select a gender.');
    if (!formData.day || !formData.month || !formData.year)
      return setError('Please complete your date of birth.');
    if (!formData.time) return setError('Please enter your time of birth.');
    if (!formData.latitude || !formData.longitude)
      return setError('Please pick your birth place from the suggestions list.');

    const date = `${formData.year}-${String(formData.month).padStart(2, '0')}-${String(
      formData.day
    ).padStart(2, '0')}`;

    onSubmit({ ...formData, name: formData.name.trim(), date });
  };

  const hasCoords = formData.latitude !== '' && formData.longitude !== '';
  const currentYear = new Date().getFullYear();

  return (
    <div className="birth-details-form">
      <div className="form-card">
        <div className="form-card-glow" aria-hidden="true" />

        <div className="form-head">
          <span className="form-eyebrow">Step 1 of 2</span>
          <h2>Your birth details</h2>
          <p className="form-subtitle">
            Vedic calculation is sensitive to the minute and the meridian — the
            more exact these are, the more precise your chart.
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setField('name', e.target.value)}
              placeholder="Who is this chart for?"
              autoComplete="name"
              maxLength={60}
            />
            <small className="field-hint">
              Used to address you in the chat and to label this chart.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="gender">Gender</label>
            <div className="segmented">
              {['male', 'female'].map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`segment ${formData.gender === option ? 'is-on' : ''}`}
                  onClick={() => setField('gender', option)}
                  aria-pressed={formData.gender === option}
                >
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Date of birth</label>
            <div className="date-dropdowns">
              <div className="select-wrap">
                <select value={formData.day} onChange={(e) => setField('day', e.target.value)}>
                  <option value="">Day</option>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
              </div>

              <div className="select-wrap">
                <select value={formData.month} onChange={(e) => setField('month', e.target.value)}>
                  <option value="">Month</option>
                  {MONTHS.map((label, i) => (
                    <option key={label} value={i + 1}>{label}</option>
                  ))}
                </select>
              </div>

              <div className="select-wrap">
                <select value={formData.year} onChange={(e) => setField('year', e.target.value)}>
                  <option value="">Year</option>
                  {Array.from({ length: 110 }, (_, i) => currentYear - i).map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="time">Time of birth</label>
            <input
              type="time"
              id="time"
              value={formData.time}
              onChange={(e) => setField('time', e.target.value)}
            />
            <small className="field-hint">
              As close to the recorded minute as you can manage.
            </small>
          </div>

          <div className="form-group location-group">
            <label htmlFor="location">Place of birth</label>
            <div className="location-input-wrap">
              <input
                type="text"
                id="location"
                value={formData.location}
                onChange={handleLocationChange}
                placeholder="Start typing a city…"
                autoComplete="off"
              />
              {isSearching && <span className="location-spinner" aria-hidden="true" />}
              {hasCoords && !isSearching && (
                <span className="location-check" aria-hidden="true">✓</span>
              )}
            </div>

            {showSuggestions && locationSuggestions.length > 0 && (
              <ul className="location-suggestions">
                {locationSuggestions.map((suggestion, index) => (
                  <li key={index}>
                    <button
                      type="button"
                      className="suggestion-item"
                      onClick={() => selectLocation(suggestion)}
                    >
                      {suggestion.display_name}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {hasCoords && (
              <small className="coordinates">
                {Number(formData.latitude).toFixed(4)}°, {Number(formData.longitude).toFixed(4)}°
              </small>
            )}
          </div>

          {error && <div className="form-error" role="alert">{error}</div>}

          <button type="submit" className="submit-btn">
            <span>Calculate my chart</span>
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path d="M3 8h9M8.5 4.5L12 8l-3.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </form>

        <div className="privacy-note">
          Stored only in this browser. Nothing is uploaded or kept on our servers.
        </div>
      </div>
    </div>
  );
}

export default BirthDetailsForm;
